import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Loader2, ShieldOff, Send, Trash2, RefreshCw, Instagram, Facebook,
  CheckCircle2, AlertCircle, Save, Image as ImageIcon, FileText, Plus, ArrowLeft, X,
  Heart, MessageCircle, MoreVertical, Trophy,
} from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import CreateSocialPostModal from '@/components/admin/CreateSocialPostModal';
import CreateCompetitionPostModal from '@/components/admin/CreateCompetitionPostModal';
import TikTokFeedbackView, { TikTokIcon } from '@/components/admin/TikTokFeedbackView';
import { useAuth } from '@/lib/AuthContext';
import ConfirmDialog from '@/components/admin/ConfirmDialog';

const TABS = [
  { key: 'posted', label: 'POSTED', match: (s) => s === 'posted' || s === 'partially_posted' },
  { key: 'tiktok', label: 'TIKTOK', match: () => false },
  { key: 'draft', label: 'DRAFTS', match: (s) => s === 'draft' },
  { key: 'failed', label: 'FAILED', match: (s) => s === 'failed' },
  { key: 'all', label: 'ALL', match: () => true },
];

const SOURCE_LABEL = { product: 'PRODUCT', experience: 'EXPERIENCE', review: 'REVIEW', manual: 'MANUAL', competition: 'COMPETITION' };
const SOURCE_COLOR = { product: 'text-primary', experience: 'text-emerald-400', review: 'text-amber-400', manual: 'text-violet-400', competition: 'text-yellow-400' };
const FB_CONNECTOR_ID = '6a8d89b19d7d9ed5ca753f8a';

const openPopup = (url) => {
  if (!url) return;
  window.open(url, 'social-post-popup', 'noopener,noreferrer,width=480,height=800,scrollbars=yes,resizable=yes');
};

export default function AdminSocialPosts() {
  const { user: currentUser, isLoadingAuth: authLoading } = useAuth();
  const [tab, setTab] = useState('posted');
  const [publishing, setPublishing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showCompetitionCreate, setShowCompetitionCreate] = useState(false);
  const [edits, setEdits] = useState({});
  const [statusMsg, setStatusMsg] = useState(null);
  const [disconnectTarget, setDisconnectTarget] = useState(null);
  const [showFbDebug, setShowFbDebug] = useState(false);
  const [publishError, setPublishError] = useState(null);
  const queryClient = useQueryClient();

  const { data: posts = [], isLoading } = useQuery({
    queryKey: ['admin-social-posts'],
    queryFn: () => base44.entities.SocialPost.list('-created_date', 500),
    enabled: !!currentUser && currentUser.role === 'admin',
  });

  const { data: fbConn, refetch: refetchFb } = useQuery({
    queryKey: ['facebook-connection'],
    queryFn: async () => {
      try {
        const res = await base44.functions.invoke('checkFacebookConnection', {});
        return res.data;
      } catch {
        return { connected: false };
      }
    },
  });

  const { data: igConn } = useQuery({
    queryKey: ['instagram-connection'],
    queryFn: async () => {
      try {
        const res = await base44.functions.invoke('checkInstagramConnection', {});
        return res.data;
      } catch {
        return { connected: false };
      }
    },
  });

  const { isFetching: fetchingEngagement, refetch: refetchEngagement } = useQuery({
    queryKey: ['social-engagement'],
    queryFn: async () => {
      try {
        const res = await base44.functions.invoke('refreshSocialEngagement', {});
        queryClient.invalidateQueries({ queryKey: ['admin-social-posts'] });
        return res.data;
      } catch {
        return null;
      }
    },
    enabled: tab === 'posted' && !!currentUser && currentUser.role === 'admin',
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: tiktokData, isLoading: tiktokLoading, refetch: refetchTiktok } = useQuery({
    queryKey: ['tiktok-videos'],
    queryFn: async () => {
      try {
        const res = await base44.functions.invoke('listTikTokVideos', {});
        return res.data;
      } catch {
        return { connected: false, videos: [] };
      }
    },
    enabled: !!currentUser && currentUser.role === 'admin',
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: socialSettings } = useQuery({
    queryKey: ['social-settings'],
    queryFn: () => base44.entities.SocialSettings.list(),
    enabled: !!currentUser && currentUser.role === 'admin',
  });
  const fbSettings = (socialSettings || []).find((s) => s.setting_key === 'global') || (socialSettings || [])[0];

  const handleSelectFbPage = async (page) => {
    try {
      if (fbSettings?.id) {
        await base44.entities.SocialSettings.update(fbSettings.id, { fb_page_id: page.id, fb_page_name: page.name });
      } else {
        await base44.entities.SocialSettings.create({ setting_key: 'global', fb_page_id: page.id, fb_page_name: page.name });
      }
      queryClient.invalidateQueries({ queryKey: ['social-settings'] });
      showMsg('success', `Posting to: ${page.name}`);
    } catch (e) {
      showMsg('error', e.message || 'Failed to save Page selection.');
    }
  };

  const showMsg = (type, message) => {
    setStatusMsg({ type, message });
    setTimeout(() => setStatusMsg(null), 5000);
  };

  const handleConnectFb = async () => {
    try {
      const url = await base44.connectors.connectAppUser(FB_CONNECTOR_ID);
      const popup = window.open(url, '_blank');
      const timer = setInterval(() => {
        if (!popup || popup.closed) {
          clearInterval(timer);
          refetchFb();
        }
      }, 500);
    } catch (e) {
      showMsg('error', e.message || 'Could not start Facebook connection.');
    }
  };

  const handleDisconnectFb = async () => {
    try {
      await base44.connectors.disconnectAppUser(FB_CONNECTOR_ID);
      refetchFb();
      showMsg('success', 'Facebook disconnected.');
    } catch (e) {
      showMsg('error', e.message || 'Failed to disconnect.');
    }
  };

  const confirmDisconnect = async () => {
    const target = disconnectTarget;
    setDisconnectTarget(null);
    if (target === 'facebook') {
      await handleDisconnectFb();
    }
    // instagram: shared workspace connection — no frontend disconnect; modal explains where to manage it.
  };

  const currentTab = TABS.find((t) => t.key === tab);
  const filtered = posts.filter((p) => currentTab.match(p.status));

  const handleSaveCaption = async (post) => {
    const caption = edits[post.id] ?? post.caption;
    try {
      await base44.entities.SocialPost.update(post.id, { caption });
      showMsg('success', 'Caption saved.');
      queryClient.invalidateQueries({ queryKey: ['admin-social-posts'] });
    } catch (e) {
      showMsg('error', e.message || 'Failed to save caption.');
    }
  };

  const handleToggle = async (post, field) => {
    const key = field === 'ig' ? 'post_to_instagram' : 'post_to_facebook';
    try {
      await base44.entities.SocialPost.update(post.id, { [key]: !post[key] });
      queryClient.invalidateQueries({ queryKey: ['admin-social-posts'] });
    } catch (e) {
      showMsg('error', e.message || 'Failed to update.');
    }
  };

  const handlePublish = async (post) => {
    setPublishing(post.id);
    try {
      // persist any pending caption edit first
      if (edits[post.id] != null && edits[post.id] !== post.caption) {
        await base44.entities.SocialPost.update(post.id, { caption: edits[post.id] });
      }
      // Instagram rejects unsupported aspect ratios — auto-crop server-side to a safe ratio first
      let adjusted = false;
      if (post.post_to_instagram && post.image_url && !post.ig_post_id) {
        const prep = await base44.functions.invoke('prepareInstagramImage', { image_url: post.image_url });
        const { url, adjusted: adj, error } = prep.data || {};
        if (error) {
          setPublishError(`Could not prepare the image for Instagram:\n${error}`);
          return;
        }
        if (adj && url && url !== post.image_url) {
          await base44.entities.SocialPost.update(post.id, { image_url: url });
          adjusted = true;
        }
      }
      const res = await base44.functions.invoke('publishSocialPost', { post_id: post.id });
      const { status, results, errors } = res.data;
      if (status === 'posted') {
        showMsg('success', `Posted to Instagram${results.fb ? ' & Facebook' : ''}${adjusted ? ' (image auto-cropped for Instagram)' : ''}.`);
      } else if (status === 'partially_posted') {
        setPublishError(`Partially posted — the following platform(s) failed:\n${errors.join(' | ')}`);
      } else {
        setPublishError(errors.join(' | ') || 'Publish failed.');
      }
      queryClient.invalidateQueries({ queryKey: ['admin-social-posts'] });
    } catch (e) {
      setPublishError(e.message || 'Publish failed.');
    } finally {
      setPublishing(null);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await base44.entities.SocialPost.delete(deleteTarget.id);
      queryClient.invalidateQueries({ queryKey: ['admin-social-posts'] });
      setDeleteTarget(null);
    } catch (e) {
      showMsg('error', e.message || 'Failed to delete.');
    }
  };

  const handleArchive = async (post) => {
    try {
      await base44.entities.SocialPost.update(post.id, { status: 'archived' });
      queryClient.invalidateQueries({ queryKey: ['admin-social-posts'] });
    } catch (e) {
      showMsg('error', e.message || 'Failed to archive.');
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      {/* Header */}
      <section className="py-14 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <p className="text-primary text-xs font-bold tracking-widest mb-2">MARKETING &amp; COMMS</p>
          <h1 className="text-white font-black text-3xl md:text-4xl uppercase">SOCIAL POSTS</h1>
          <p className="text-white/30 text-sm mt-1">
            Review and approve auto-generated drafts for Instagram &amp; Facebook.
          </p>
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <span className="flex items-center gap-1.5 text-xs text-green-400">
              <Instagram className="w-3.5 h-3.5" />
              {igConn?.connected && igConn?.username
                ? `@${igConn.username}`
                : 'Instagram connected'}
            </span>
            <button
              onClick={() => setDisconnectTarget('instagram')}
              className="flex items-center justify-center w-5 h-5 text-red-400/70 hover:text-red-400 hover:bg-red-500/10 transition-colors"
              title="Disconnect Instagram"
            >
              <X className="w-3.5 h-3.5" />
            </button>
            <span className="text-white/20">·</span>
            {fbConn?.connected ? (
              <>
                {fbConn?.pages?.length ? (
                  <span className="flex items-center gap-1.5 text-xs text-green-400">
                    <Facebook className="w-3.5 h-3.5" />
                    <select
                      value={fbSettings?.fb_page_id || fbConn.pages[0]?.id || ''}
                      onChange={(e) => {
                        const p = fbConn.pages.find((x) => x.id === e.target.value);
                        if (p) handleSelectFbPage(p);
                      }}
                      className="bg-[#0d0d0f] border border-white/10 text-green-400 text-xs px-2 py-1 rounded-sm"
                    >
                      {fbConn.pages.map((p) => (
                        <option key={p.id} value={p.id} className="text-white bg-[#0d0d0f]">{p.name}</option>
                      ))}
                    </select>
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-xs text-green-400">
                    <Facebook className="w-3.5 h-3.5" /> Facebook connected
                  </span>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="flex items-center justify-center w-5 h-5 text-white/40 hover:text-white transition-colors"
                      title="Facebook options"
                    >
                      <MoreVertical className="w-3.5 h-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="bg-[#111115] border-white/10">
                    <DropdownMenuItem onClick={() => refetchFb()} className="text-white/70 hover:text-white focus:text-white cursor-pointer">
                      <RefreshCw className="w-3.5 h-3.5 mr-2" /> Refresh Pages
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setShowFbDebug((v) => !v)} className="text-white/70 hover:text-white focus:text-white cursor-pointer">
                      <AlertCircle className="w-3.5 h-3.5 mr-2" /> {showFbDebug ? 'Hide token debug' : 'Token debug'}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <button
                  onClick={() => setDisconnectTarget('facebook')}
                  className="flex items-center justify-center w-5 h-5 text-red-400/70 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                  title="Disconnect Facebook"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
                {fbConn?.pages && fbConn.pages.length === 0 && (
                  <span className="flex items-center gap-1 text-xs text-amber-400">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {fbConn.graph_error ? fbConn.graph_error : 'Connected but no Pages — check your Facebook app has pages_show_list permission & your account manages a Page'}
                  </span>
                )}
              </>
            ) : (
              <button
                onClick={handleConnectFb}
                className="flex items-center gap-1.5 text-xs text-amber-400 hover:text-white transition-colors"
              >
                <Facebook className="w-3.5 h-3.5" /> Connect Facebook
              </button>
            )}
            <span className="text-white/20">·</span>
            <span className={`flex items-center gap-1.5 text-xs ${tiktokData?.connected ? 'text-green-400' : 'text-amber-400'}`}>
              <TikTokIcon className="w-3.5 h-3.5" />
              {tiktokData?.connected ? 'TikTok connected' : 'TikTok not connected'}
            </span>
            <button
              onClick={() => setShowCompetitionCreate(true)}
              className="flex items-center gap-1.5 border border-yellow-400/40 text-yellow-400 hover:bg-yellow-400/10 font-bold text-xs tracking-wider px-4 py-2 transition-colors ml-auto"
            >
              <Trophy className="w-3.5 h-3.5" /> COMPETITION POST
            </button>
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wider px-4 py-2 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> NEW POST
            </button>
          </div>

          {showFbDebug && fbConn?.connected && (
            <div className="mt-3 border border-white/10 bg-[#0d0d0f] p-4 text-xs font-mono">
              {fbConn.fb_user && (
                <>
                  <p className="text-white/40 mb-1">Facebook user behind stored token:</p>
                  <p className="text-white/70 mb-3"><span className="text-blue-400">{fbConn.fb_user.name}</span> — {fbConn.fb_user.id}</p>
                </>
              )}
              <p className="text-white/40 mb-2">Pages returned by the stored app token ({fbConn.pages?.length || 0}):</p>
              {fbConn.pages?.length ? (
                <ul className="space-y-1">
                  {fbConn.pages.map((p) => (
                    <li key={p.id} className="text-white/70">
                      <span className="text-green-400">{p.name}</span> — {p.id}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-red-400">No pages returned. {fbConn.graph_error || ''}</p>
              )}
              {fbConn.permissions?.length ? (
                <>
                  <p className="text-white/40 mt-3 mb-1">Token permissions:</p>
                  <p className="text-white/50">{fbConn.permissions.join(', ')}</p>
                </>
              ) : null}
            </div>
          )}

          {statusMsg && (
            <div className={`flex items-start gap-3 px-4 py-3 rounded-sm mt-4 ${
              statusMsg.type === 'success' ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'
            }`}>
              {statusMsg.type === 'success'
                ? <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
                : <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />}
              <p className={`text-sm font-medium ${statusMsg.type === 'success' ? 'text-green-300' : 'text-red-300'}`}>
                {statusMsg.message}
              </p>
            </div>
          )}
        </div>
      </section>

      {showCreate && (
        <CreateSocialPostModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            queryClient.invalidateQueries({ queryKey: ['admin-social-posts'] });
          }}
        />
      )}

      {showCompetitionCreate && (
        <CreateCompetitionPostModal
          onClose={() => setShowCompetitionCreate(false)}
          onCreated={() => {
            setShowCompetitionCreate(false);
            queryClient.invalidateQueries({ queryKey: ['admin-social-posts'] });
          }}
        />
      )}

      {disconnectTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setDisconnectTarget(null)}>
          <div className="bg-[#111115] border border-white/10 max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
            {disconnectTarget === 'facebook' ? (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-5 h-5 text-red-400" />
                  <h3 className="text-white font-bold text-base">Disconnect Facebook?</h3>
                </div>
                <p className="text-white/50 text-sm mb-5">
                  You won't be able to post to your Facebook Page until you reconnect. Already-published posts are not affected.
                </p>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setDisconnectTarget(null)} className="text-xs font-bold px-4 py-2 text-white/60 hover:text-white border border-white/10 hover:border-white/30 transition-colors">CANCEL</button>
                  <button onClick={confirmDisconnect} className="text-xs font-bold px-4 py-2 bg-red-500 hover:bg-red-600 text-white transition-colors">DISCONNECT</button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <Instagram className="w-5 h-5 text-white/60" />
                  <h3 className="text-white font-bold text-base">Instagram connection</h3>
                </div>
                <p className="text-white/50 text-sm mb-5">
                  Instagram is a shared workspace connection and can't be disconnected from here. Manage it in <span className="text-white/70">Workspace Settings → Connectors</span>.
                </p>
                <div className="flex justify-end">
                  <button onClick={() => setDisconnectTarget(null)} className="text-xs font-bold px-4 py-2 text-white/60 hover:text-white border border-white/10 hover:border-white/30 transition-colors">OK</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {publishError && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setPublishError(null)}>
          <div className="bg-[#111115] border border-red-500/30 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="w-5 h-5 text-red-400" />
              <h3 className="text-white font-bold text-base">PUBLISH FAILED</h3>
            </div>
            <p className="text-red-300 text-sm whitespace-pre-wrap mb-5">{publishError}</p>
            <div className="flex justify-end">
              <button onClick={() => setPublishError(null)} className="text-xs font-bold px-4 py-2 bg-red-500 hover:bg-red-600 text-white transition-colors">DISMISS</button>
            </div>
          </div>
        </div>
      )}

      <section className="px-6 lg:px-10 py-10 max-w-6xl mx-auto">
        {/* Tabs */}
        <div className="flex items-center gap-1 mb-6 border-b border-white/5">
          {TABS.map((t) => {
            const count = t.key === 'tiktok' ? (tiktokData?.videos?.length ?? 0) : posts.filter((p) => t.match(p.status)).length;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2.5 text-xs font-bold tracking-wider transition-colors border-b-2 ${
                  tab === t.key ? 'text-primary border-primary' : 'text-white/40 border-transparent hover:text-white/70'
                }`}
              >
                {t.label} <span className="text-white/20 ml-1">{count}</span>
              </button>
            );
          })}
          <button
            onClick={() => refetchEngagement()}
            disabled={fetchingEngagement}
            className="ml-auto flex items-center gap-1.5 text-xs font-bold px-3 py-2 text-white/40 hover:text-primary border border-white/10 hover:border-primary/30 transition-colors disabled:opacity-50"
            title="Refresh Instagram likes & comments"
          >
            {fetchingEngagement ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Heart className="w-3.5 h-3.5" />}
            ENGAGEMENT
          </button>
          <button
            onClick={() => queryClient.invalidateQueries({ queryKey: ['admin-social-posts'] })}
            className="p-2 text-white/30 hover:text-white transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {tab === 'tiktok' ? (
          <TikTokFeedbackView data={tiktokData} loading={tiktokLoading} onRefresh={refetchTiktok} />
        ) : isLoading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 border border-white/5 bg-[#111115]">
            <FileText className="w-10 h-10 text-white/10 mx-auto mb-3" />
            <p className="text-white/30 text-sm">No {tab === 'all' ? '' : tab + ' '}posts yet.</p>
            <p className="text-white/20 text-xs mt-1">Drafts are auto-created when you add featured/manual products, new experiences, or approve reviews.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((post) => {
              const isDraft = post.status === 'draft';
              const isBusy = publishing === post.id;
              return (
                <div key={post.id} className="border border-white/5 bg-[#111115] p-5">
                  {/* Top row: source + status */}
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <span className={`text-[10px] font-bold tracking-widest ${SOURCE_COLOR[post.source_type] || 'text-white/40'}`}>
                        {SOURCE_LABEL[post.source_type] || post.source_type}
                      </span>
                      <span className="text-white text-sm font-bold">{post.source_label || 'Untitled'}</span>
                      {post.created_by_name && (
                        <span className="text-white/20 text-[10px]">by {post.created_by_name}</span>
                      )}
                    </div>
                    <StatusBadge status={post.status} />
                  </div>

                  {/* Image */}
                  {post.image_url ? (
                    <div className="w-full max-w-[300px] h-40 bg-[#0d0d0f] border border-white/5 mb-3 overflow-hidden">
                      <img src={post.image_url} alt="" className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    isDraft && post.post_to_instagram && (
                      <div className="flex items-center gap-2 text-amber-400/70 text-xs mb-3">
                        <ImageIcon className="w-3.5 h-3.5" /> No image — Instagram will be skipped on publish (IG requires an image).
                      </div>
                    )
                  )}

                  {/* Caption (editable for drafts) */}
                  {isDraft ? (
                    <Textarea
                      value={edits[post.id] ?? post.caption}
                      onChange={(e) => setEdits((p) => ({ ...p, [post.id]: e.target.value }))}
                      rows={5}
                      className="bg-[#0d0d0f] border-white/10 text-white text-sm resize-y"
                    />
                  ) : (
                    <p className="text-white/60 text-sm whitespace-pre-wrap bg-[#0d0d0f] border border-white/5 p-3">
                      {post.caption}
                    </p>
                  )}

                  {/* Platform results for posted/failed */}
                  {!isDraft && (
                    <div className="flex flex-wrap gap-3 mt-3 text-xs">
                      {post.ig_post_id && (
                        post.ig_permalink ? (
                          <a
                            href={post.ig_permalink}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => { e.preventDefault(); openPopup(post.ig_permalink); }}
                            className="flex items-center gap-1.5 text-green-400 hover:underline cursor-pointer"
                            title="Open post on Instagram"
                          >
                            <Instagram className="w-3.5 h-3.5" /> IG: {igConn?.username ? `@${igConn.username}` : 'Instagram'}
                          </a>
                        ) : (
                          <span
                            className="flex items-center gap-1.5 text-green-400 cursor-default"
                            title={`Media ID: ${post.ig_post_id} — refresh engagement to load the link`}
                          >
                            <Instagram className="w-3.5 h-3.5" /> IG: {igConn?.username ? `@${igConn.username}` : 'Instagram'}
                          </span>
                        )
                      )}
                      {post.ig_post_id && typeof post.ig_likes === 'number' && (
                        <span className="flex items-center gap-1.5 text-white/50">
                          <Heart className="w-3.5 h-3.5 text-rose-400" /> {post.ig_likes ?? 0}
                          <MessageCircle className="w-3.5 h-3.5 ml-1.5 text-sky-400" /> {post.ig_comments ?? 0}
                        </span>
                      )}
                      {post.fb_post_id && (() => {
                        const parts = post.fb_post_id.split('_');
                        const pageId = parts[0];
                        const postId = parts[1];
                        const page = fbConn?.pages?.find((p) => p.id === pageId);
                        const pageName = page?.name || fbSettings?.fb_page_name || 'Facebook Page';
                        const fbUrl = postId ? `https://www.facebook.com/${pageId}/posts/${postId}` : '';
                        return fbUrl ? (
                          <a
                            href={fbUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => { e.preventDefault(); openPopup(fbUrl); }}
                            className="flex items-center gap-1.5 text-green-400 hover:underline cursor-pointer"
                            title="Open post on Facebook"
                          >
                            <Facebook className="w-3.5 h-3.5" /> FB: {pageName}
                          </a>
                        ) : (
                          <span
                            className="flex items-center gap-1.5 text-green-400 cursor-default"
                            title={`Post ID: ${post.fb_post_id}`}
                          >
                            <Facebook className="w-3.5 h-3.5" /> FB: {pageName}
                          </span>
                        );
                      })()}
                      {post.fb_post_id && typeof post.fb_likes === 'number' && (
                        <span className="flex items-center gap-1.5 text-white/50">
                          <Heart className="w-3.5 h-3.5 text-rose-400" /> {post.fb_likes ?? 0}
                          <MessageCircle className="w-3.5 h-3.5 ml-1.5 text-sky-400" /> {post.fb_comments ?? 0}
                        </span>
                      )}
                      {post.error && (
                        <span className="flex items-start gap-1.5 text-red-400">
                          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {post.error}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-2 mt-4 flex-wrap">
                    {isDraft && (
                      <>
                        <button
                          onClick={() => handleToggle(post, 'ig')}
                          className={`flex items-center gap-1.5 text-xs font-bold px-3 py-2 border transition-colors ${
                            post.post_to_instagram
                              ? 'text-primary border-primary/40 bg-primary/10'
                              : 'text-white/30 border-white/10 hover:border-white/30'
                          }`}
                        >
                          <Instagram className="w-3.5 h-3.5" /> IG {post.post_to_instagram ? 'ON' : 'OFF'}
                        </button>
                        <button
                          onClick={() => handleToggle(post, 'fb')}
                          className={`flex items-center gap-1.5 text-xs font-bold px-3 py-2 border transition-colors ${
                            post.post_to_facebook
                              ? 'text-blue-400 border-blue-400/40 bg-blue-400/10'
                              : 'text-white/30 border-white/10 hover:border-white/30'
                          }`}
                        >
                          <Facebook className="w-3.5 h-3.5" /> FB {post.post_to_facebook ? 'ON' : 'OFF'}
                        </button>
                        <button
                          onClick={() => handleSaveCaption(post)}
                          className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 text-white/60 hover:text-white border border-white/10 hover:border-white/30 transition-colors"
                        >
                          <Save className="w-3.5 h-3.5" /> SAVE
                        </button>
                        <button
                          onClick={() => handlePublish(post)}
                          disabled={isBusy}
                          className="flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wider px-4 py-2 transition-colors disabled:opacity-50 ml-auto"
                        >
                          {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                          APPROVE &amp; PUBLISH
                        </button>
                      </>
                    )}
                    {!isDraft && (post.status === 'partially_posted' || post.status === 'failed') && (
                      <button
                        onClick={() => handlePublish(post)}
                        disabled={isBusy}
                        className="flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wider px-4 py-2 transition-colors disabled:opacity-50 ml-auto"
                      >
                        {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                        RETRY POST
                      </button>
                    )}
                    {!isDraft && post.status !== 'archived' && (
                      <button
                        onClick={() => handleArchive(post)}
                        className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 text-white/40 hover:text-white border border-white/10 hover:border-white/30 transition-colors ml-auto"
                      >
                        ARCHIVE
                      </button>
                    )}
                    <button
                      onClick={() => setDeleteTarget(post)}
                      className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 text-white/30 hover:text-red-400 border border-white/10 hover:border-red-400/30 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> DELETE
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete social post"
        description={deleteTarget ? `Delete "${deleteTarget.source_label || 'this post'}"? This cannot be undone.` : ''}
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    draft: 'bg-white/5 text-white/50',
    posting: 'bg-blue-400/10 text-blue-400',
    posted: 'bg-green-500/10 text-green-400',
    partially_posted: 'bg-amber-400/10 text-amber-400',
    failed: 'bg-red-500/10 text-red-400',
    archived: 'bg-white/5 text-white/30',
  };
  return (
    <span className={`text-[10px] font-bold tracking-widest px-2 py-1 ${map[status] || 'bg-white/5 text-white/40'}`}>
      {status.replace('_', ' ').toUpperCase()}
    </span>
  );
}